// src/hooks/useGameSocket.js
import { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';

const SOCKET_SERVER_URL = process.env.REACT_APP_SOCKET_URL || 'https://adey.lucky.et';

/**
 * Custom hook for managing Socket.IO connection and events for a Bingo game.
 * Handles connection, disconnection, reconnection, and all game-related events.
 * * @param {string} gameId The ID of the game to connect to
 * @param {object} socketEventHandlers Object with event handlers for socket events
 * @param {function} cleanupAndNavigate Callback for cleanup and navigation on errors
 * @returns {{socketConnected: boolean, emit: function, gameData: object}} Connection status, emit function, and game data
 */
const useGameSocket = (gameId, socketEventHandlers, cleanupAndNavigate) => {
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [gameData, setGameData] = useState(null); // State to hold the latest game data
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Stable emit function that can be used throughout the component
  const emit = useCallback((eventName, data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(eventName, data);
      return true;
    }
    if (socketEventHandlers.error) {
      socketEventHandlers.error({ 
        message: `Socket not connected. Cannot emit ${eventName}. Please refresh.`,
        type: 'socketNotConnected'
      }, emit);
    }
    return false;
  }, [socketEventHandlers]);

  // Memoized handlers for core game events
  const handleGameUpdate = useCallback((data) => {
    setGameData(data);
    if (socketEventHandlers.gameUpdate) {
      socketEventHandlers.gameUpdate(data, emit);
    }
  }, [emit, socketEventHandlers]);

  const handleGameEnd = useCallback((data) => {
    // Game ended, update gameData for the final state
    setGameData(prevData => ({ ...prevData, gameStatus: data.status, winnerInfo: data }));
    if (socketEventHandlers.gameEnd) {
      socketEventHandlers.gameEnd(data, emit);
    }
  }, [emit, socketEventHandlers]);

  const handleConnectionStatus = useCallback(({ status, reason }) => {
    setSocketConnected(status === 'connected');
    if (socketEventHandlers.connectionStatus) {
      socketEventHandlers.connectionStatus({ status, reason }, emit);
    }
  }, [emit, socketEventHandlers]);

  useEffect(() => {
    if (!gameId) {
      return;
    }

    // Ensure previous socket is disconnected before creating a new one
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      handleConnectionStatus({ status: 'disconnected', reason: 'unauthorized' });
      cleanupAndNavigate('/login');
      return;
    }

    socketRef.current = io(SOCKET_SERVER_URL, {
      auth: { token },
      query: { gameId }, // Pass gameId as a query parameter
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 1000, // 1 second
      reconnectionDelayMax: 5000, // 5 seconds
      timeout: 20000, // 20 seconds
    });

    // Event listeners
    socketRef.current.on('connect', () => {
      setSocketConnected(true);
      reconnectAttempts.current = 0; // Reset attempts on successful connection
      handleConnectionStatus({ status: 'connected' });
      // Join the game room after successful connection
      socketRef.current.emit('joinGame', { gameId });
    });

    socketRef.current.on('disconnect', (reason) => {
      setSocketConnected(false);
      handleConnectionStatus({ status: 'disconnected', reason });
    });

    socketRef.current.on('reconnect_attempt', (attemptNumber) => {
      reconnectAttempts.current = attemptNumber;
      handleConnectionStatus({ status: 'reconnecting', reason: `Attempt ${attemptNumber}` });
    });

    socketRef.current.on('reconnect_failed', () => {
      handleConnectionStatus({ status: 'failed', reason: 'Reconnection failed' });
      cleanupAndNavigate('/dashboard'); // Navigate away if reconnection fails permanently
    });

    socketRef.current.on('reconnect', (attemptNumber) => {
      setSocketConnected(true);
      reconnectAttempts.current = 0;
      handleConnectionStatus({ status: 'connected' });
      // Re-join the game room on reconnect
      socketRef.current.emit('joinGame', { gameId });
    });

    socketRef.current.on('connect_error', (error) => {
      handleConnectionStatus({ status: 'failed', reason: error.message });
      // If unauthorized, navigate to login
      if (error.message === 'No token provided.' || error.message === 'Authentication failed.') {
        cleanupAndNavigate('/login');
      }
    });

    // General error handler
    socketRef.current.on('error', (error) => {
      if (socketEventHandlers.error) {
        socketEventHandlers.error(error, emit);
      }
    });

    // Register primary game event handlers
    socketRef.current.on('gameUpdate', handleGameUpdate);
    socketRef.current.on('gameEnd', handleGameEnd); // Listen for gameEnd event directly

    // Register all custom event handlers
    for (const [eventName, handler] of Object.entries(socketEventHandlers)) {
      // Avoid re-registering internal handlers or handlers managed internally
      if (typeof handler === 'function' && !['connectionStatus', 'error', 'gameUpdate', 'gameEnd'].includes(eventName)) {
        socketRef.current.on(eventName, (data) => handler(data, emit));
      }
    }

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('disconnect');
        socketRef.current.off('reconnect_attempt');
        socketRef.current.off('reconnect_failed');
        socketRef.current.off('reconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('error');

        // Remove primary game event handlers
        socketRef.current.off('gameUpdate', handleGameUpdate);
        socketRef.current.off('gameEnd', handleGameEnd);

        // Remove all custom event handlers (including the now removed markNumber and callBingo)
        for (const eventName in socketEventHandlers) {
          if (typeof socketEventHandlers[eventName] === 'function') {
            socketRef.current.off(eventName);
          }
        }

        if (socketRef.current.connected) {
          socketRef.current.disconnect();
        }
        socketRef.current = null;
      }
    };
  }, [gameId, cleanupAndNavigate, emit, socketEventHandlers, handleConnectionStatus, handleGameUpdate, handleGameEnd]);

  return { socketConnected, emit, gameData };
};

export default useGameSocket;
